import React, { useMemo } from 'react'
import { Collapse, Stack, styled, useMediaQuery, useTheme } from '@mui/material'

import { PaddockRow } from './PaddockRow'
import { SeedMix } from '../types/SeedOrderFormType'
import Typography from '../../../components/Typography'
import { ExpandIcon } from '../components/ExpandIcon'
import { QuantityChip } from '../../dashboard/components/QuantityChip'
import { allPaddocksHectares } from '../utils/calculateTotal'
import { selectCurrentFarmerAccount } from '../../azure-b2c/storage'
import { setSeedValue } from '../storage/seedCart.slice'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../store/rootStore'
import _ from 'lodash'

const PaddocksTitleBox = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'flex-start',
    gap: '24px',
  },
}))

export const PaddockForm = ({ seedMix }: { seedMix: SeedMix }) => {
  const account = useSelector(selectCurrentFarmerAccount)
  const dispatch = useAppDispatch()
  const showPaddock = seedMix.paddocksExpanded

  const setShowPaddock = (show: boolean) => {
    const accountNumber = account?.accountNumber
    if (accountNumber) {
      dispatch(
        setSeedValue({
          accountNumber,
          key: 'paddocksExpanded',
          value: show,
          seedMix,
        }),
      )
    }
  }

  const paddocks = seedMix.paddocks

  const totalHectares = useMemo(() => allPaddocksHectares(paddocks), [paddocks])

  const removePaddock = (index: number) => {
    const accountNumber = account?.accountNumber
    if (accountNumber) {
      const copy = _.cloneDeep(seedMix)
      if (index >= 0 && index < copy.paddocks.length) {
        copy.paddocks.splice(index, 1)
        dispatch(
          setSeedValue({
            accountNumber,
            key: '',
            value: copy,
            seedMix,
          }),
        )
      } else {
        console.error('Invalid index: Unable to remove paddock')
      }
    }
  }

  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))

  const paddocksMemo = useMemo(() => {
    return seedMix.paddocks.map((field, index) => {
      return <PaddockRow index={index} key={field.name} remove={removePaddock} seedMix={seedMix} />
    })
  }, [seedMix.paddocks])

  return (
    <Stack px={2} py={3}>
      <PaddocksTitleBox>
        <Stack direction='row' width='100%' justifyContent={'space-between'} gap={2}>
          <Stack
            direction={isSm ? 'column' : 'row'}
            spacing={3}
            justifyContent={'space-between'}
            width={isSm ? 'auto' : '100%'}
          >
            <Stack direction='row' alignItems='center' spacing={1}>
              <Typography variant='h6' fontWeight='700' fontSize='20px'>
                Paddocks
              </Typography>
              <QuantityChip label={`${totalHectares} Ha`} />
            </Stack>
          </Stack>
          <Stack direction='row' alignItems={isSm ? 'flex-start' : 'center'}>
            {showPaddock ? (
              <ExpandIcon icon='expand_less' onClick={() => setShowPaddock(false)} />
            ) : (
              <ExpandIcon icon='expand_more' onClick={() => setShowPaddock(true)} />
            )}
          </Stack>
        </Stack>
      </PaddocksTitleBox>
      <Collapse in={showPaddock}>
        <Stack gap={4} mt={3}>
          {paddocksMemo}
        </Stack>
      </Collapse>
    </Stack>
  )
}
